import { PageContainer, Tabs, type TabItem } from '@/components';
import { ChevronLeft } from '@/design-system/icons';
import { useDeviceModels } from '@/features/device-models';
import { Outlet, useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useDeviceDetails } from '../hooks';

// Tab ids double as the last URL segment under /devices/$deviceId.
type DeviceTabId = 'overview' | 'rules' | 'assignment';
const DEFAULT_TAB: DeviceTabId = 'overview';
const DEVICE_TAB_IDS: DeviceTabId[] = ['overview', 'rules', 'assignment'];

// DeviceDetailLayout renders the header (back button, serial number, device type) with the
// URL-routed tab bar beside it, and the active tab route below.
export function DeviceDetailLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deviceId } = useParams({ strict: false }) as { deviceId: string };

  const { data: details } = useDeviceDetails(deviceId);
  const { data: deviceModels } = useDeviceModels();
  const modelName = deviceModels?.find((model) => model.id === details?.device.modelId)?.name;
  const serialNumber = details?.device.serialNumber;

  return (
    <PageContainer>
      {/* Device header and tab bar above the active tab route */}
      <div className="space-y-2 mx-auto relative h-full w-full grow flex flex-col text-onbackground">
        <header className="flex-col items-center gap-x-3">
          <button
            onClick={() => navigate({ to: '/devices' })}
            className="flex-row inline-flex justify-center items-center rounded-lg text-onsurface hover:bg-surface-darker focus:outline-none focus:bg-surface-darker opacity-80 cursor-pointer"
            aria-label={t('devices.detail.back')}
          >
            <ChevronLeft className="size-4" />
            <div className="mt-1" />
            <span>{t('devices.detail.back')}</span>
          </button>
          {/* Title block left, tab bar right (wraps below on narrow screens) */}
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
            <div className="flex flex-col">
              <h1 className="text-xl font-semibold text-onbackground">
                S/N {serialNumber ?? '—'}
              </h1>
              {modelName && <span className="text-sm text-muted">{modelName}</span>}
            </div>
            <DeviceTabBar deviceId={deviceId} />
          </div>
        </header>

        <Outlet />
      </div>
    </PageContainer>
  );
}

// DeviceTabBar derives the active tab from the last path segment and navigates on change.
function DeviceTabBar({ deviceId }: { deviceId: string }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const tabs: TabItem<DeviceTabId>[] = [
    { id: 'overview', label: t('devices.detail.tabs.overview') },
    { id: 'rules', label: t('devices.detail.tabs.rules') },
    { id: 'assignment', label: t('devices.detail.tabs.assignment') },
  ];

  const lastSegment = location.pathname.split('/').pop() ?? '';
  const isKnownTab = DEVICE_TAB_IDS.includes(lastSegment as DeviceTabId);
  const activeTab = isKnownTab ? (lastSegment as DeviceTabId) : DEFAULT_TAB;

  const handleTabChange = (tabId: DeviceTabId) => {
    navigate({ to: `/devices/$deviceId/${tabId}`, params: { deviceId } });
  };

  return <Tabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />;
}
